/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion } from 'motion/react';
import { Sticker, Team } from '../types';
import { STICKER_MAP, TEAMS, POS_ICON, POS_CLR } from '../constants';
import { Star } from 'lucide-react';

interface StickerProps {
  sticker: Sticker;
  small?: boolean;
  key?: string | number;
  duplicateCount?: number;
  onClick?: () => void;
}

export const StickerCard = ({ sticker, small, duplicateCount, onClick }: StickerProps) => {
  const team = sticker.teamId ? TEAMS.find(t => t.id === sticker.teamId) : null;
  const isL = sticker.rarity === 'legendary';
  const isR = sticker.rarity === 'rare';

  const width = small ? 'w-[62px]' : 'w-[96px]';
  const height = small ? 'h-[84px]' : 'h-[130px]';

  const bgStyle = team
    ? { backgroundImage: `linear-gradient(150deg, ${team.c1}ee, ${team.c1}88, ${team.c2}22)` }
    : isL ? { backgroundImage: 'linear-gradient(150deg, #1e1b4b, #312e81, #1e1b4b)' }
    : isR ? { backgroundImage: 'linear-gradient(150deg, #111827, #1f2937, #111827)' }
    : { backgroundColor: '#1e293b' };

  const borderClass = isL 
    ? 'border-blue-500/50 border-2 shadow-[0_0_15px_rgba(59,130,246,0.3)]' 
    : isR 
      ? 'border-emerald-500/50 border-2 shadow-[0_0_15px_rgba(16,185,129,0.2)]' 
      : 'border-slate-700 border';

  const renderIcon = () => {
    if (sticker.type === 'special') return <span className={`${small ? 'text-lg mt-2' : 'text-3xl mt-3'} leading-none filter drop-shadow-lg`}>{sticker.icon}</span>;
    if (sticker.type === 'badge' && team?.flag) return <img src={`https://flagcdn.com/w80/${team.flag}.png`} alt={team.name} className={`${small ? 'w-8 h-6 mt-2' : 'w-12 h-9 mt-3'} object-cover rounded-sm shadow-md`} referrerPolicy="no-referrer" crossOrigin="anonymous" />;
    return <span className={`${small ? 'text-lg mt-2' : 'text-3xl mt-3'} leading-none filter drop-shadow-lg`}>{sticker.pos ? POS_ICON[sticker.pos] : '⚽'}</span>;
  };

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={onClick ? { scale: 1.05, translateY: -2 } : {}}
      whileTap={onClick ? { scale: 0.95 } : {}}
      onClick={onClick}
      className={`${width} ${height} rounded flex flex-col items-center justify-between p-1.5 relative overflow-hidden select-none flex-shrink-0 ${borderClass} preserve-3d ${onClick ? 'cursor-pointer hover:brightness-110' : ''}`}
      style={bgStyle}
    >
      {/* Holographic Gloss Sheen Sweep */}
      <div className="hologram-gloss pointer-events-none z-10" />

      <span className="absolute top-0.5 left-1 text-[7px] text-emerald-400 font-mono font-bold tracking-tighter flex items-center gap-0.5 z-10">
        <span className="text-[5.5px] bg-emerald-500/20 px-0.5 py-0.25 rounded text-emerald-300 font-black tracking-widest text-[5px]">NFT</span>
        #{String(sticker.num).padStart(3, '0')}
      </span>

      <div className="absolute top-0.5 right-1 flex gap-0.5 opacity-80 z-10">
        {isL && <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_4px_#3b82f6]" />}
        {isR && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_#10b981]" />}
      </div>

      {renderIcon()}

      {sticker.type === 'player' && !small && team?.flag && (
        <img 
          src={`https://flagcdn.com/w20/${team.flag}.png`} 
          alt="" 
          className="absolute top-4 right-1 w-3 h-2.5 object-cover rounded-[1px] opacity-80 z-10" 
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
        />
      )}

      {sticker.type === 'player' && !small && sticker.pos && (
        <span
          className="rounded-sm px-1 py-0.5 text-[7px] text-white font-mono font-bold tracking-widest uppercase bg-slate-900/80 backdrop-blur-sm border border-slate-700 z-10"
        >
          {sticker.pos}
        </span>
      )}

      {duplicateCount !== undefined && duplicateCount > 0 && (
        <div className={`absolute ${small ? 'bottom-4.5 right-0.5 text-[6px] px-0.75' : 'bottom-6 right-1 text-[7.5px] px-1 py-0.25'} bg-amber-500 text-slate-950 font-mono font-black rounded-sm border border-amber-400 shadow-md z-10 select-none`}>
          x{duplicateCount + 1}
        </div>
      )}

      <div className="w-full bg-slate-950/40 backdrop-blur-[2px] py-1 px-1 flex items-center justify-center border-t border-white/5 gap-1 z-10">
        {team?.flag && !small && (
          <img src={`https://flagcdn.com/w20/${team.flag}.png`} alt="" className="w-2.5 h-2 object-cover rounded-[1px]" referrerPolicy="no-referrer" crossOrigin="anonymous" />
        )}
        <span className={`${small ? 'text-[6px]' : 'text-[8px]'} text-white text-center font-bold font-sans uppercase tracking-tight truncate`}>
          {sticker.name}
        </span>
      </div>
    </motion.div>
  );
};

export const EmptySlot = ({ num, small }: { num: number; small?: boolean; key?: string | number }) => {
  const width = small ? 'w-[62px]' : 'w-[96px]';
  const height = small ? 'h-[84px]' : 'h-[130px]';

  return (
    <div className={`${width} ${height} rounded flex flex-col items-center justify-center flex-shrink-0 border border-slate-800 bg-slate-900/50`}>
      <span className="text-[8px] text-slate-600 font-mono font-bold tracking-tighter">
        SLOT_{String(num).padStart(3, '0')}
      </span>
      <span className="text-xl font-mono opacity-5 text-white">??</span>
    </div>
  );
};
